// src/Components/constants/regions.ts
// 3 miền cố định dùng cho Cơ cấu Giải thưởng / Kết quả Quay số (payout-service) — khớp đúng chuỗi
// PayoutRegions bên payout-service (PayoutService/PayoutRegions.cs).

import { CacheService } from 'services/cache';
import { CacheConfig } from 'services/cache/CacheConfig';
import { SystemConfiguration } from 'pages/SystemConfiguration/types';
import { getSystemRegionApi } from 'helpers/fakebackend_helper';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('component_regions');

export const REGIONS = [st('mien_bac_text'), st('mien_trung_text'), st('mien_nam_text')] as const;
export type Region = (typeof REGIONS)[number];

// GIỮ export để tương thích ngược, NHƯNG không còn dùng làm fallback cho màn Kết quả Quay số / Cơ
// cấu Giải thưởng — 2 màn đó phải lấy Miền từ đúng 1 nguồn cấu hình (getSystemRegion), không đoán.
export const DEFAULT_REGION: Region = st('mien_bac_text');

/** Chuẩn hoá + kiểm tra 1 chuỗi có đúng là 1 trong 3 miền cố định không. */
const normalizeRegion = (value?: string | null): Region | '' => {
  // .normalize('NFC') để tránh lệch giữa 2 cách encode dấu tiếng Việt (dựng sẵn vs tổ hợp) khiến so
  // sánh strict-equal thất bại âm thầm dù nhìn giống hệt nhau.
  const v = value?.trim().normalize('NFC') ?? '';
  return (REGIONS as readonly string[]).includes(v) ? (v as Region) : '';
};

/**
 * Đọc Miền từ cache lms_cache_system_config (SystemConfiguration.region) — CHỈ để pre-fill nhanh
 * lúc component chưa kịp gọi API. KHÔNG còn fallback về "Miền Bắc": trả '' khi cache trống/không hợp
 * lệ, để màn hình biết là "chưa xác định" và chờ getSystemRegion() thay vì load nhầm dữ liệu miền khác.
 */
export const getDefaultRegion = (): Region | '' => {
  const cached = CacheService.get<SystemConfiguration>(CacheConfig.SYSTEM_CONFIG.key);
  return normalizeRegion(cached.data?.region);
};

/**
 * Yêu cầu bổ sung (README_UPDATE.md) — NGUỒN SỰ THẬT DUY NHẤT về Miền cho màn Kết quả Quay số / Cơ
 * cấu Giải thưởng. Gọi thẳng endpoint gọn GET /system-api/SystemConfiguration/region (chỉ cần đăng
 * nhập), rồi write-through lại cache lms_cache_system_config để các nơi khác dùng getDefaultRegion()
 * cũng khớp. Trả null nếu hệ thống chưa cấu hình Miền hợp lệ — màn hình KHÔNG được tự đoán, phải báo
 * lỗi cấu hình thay vì load nhầm.
 */
export const fetchSystemRegion = async (): Promise<Region | null> => {
  try {
    const res: any = await getSystemRegionApi();
    const payload = res?.data ?? res;
    const region = normalizeRegion(payload?.region ?? payload?.Region);
    if (!region) return null;

    // Đồng bộ ngược vào cache (giữ nguyên các field khác) để getDefaultRegion() ở nơi khác cũng đúng.
    try {
      const cached = CacheService.get<SystemConfiguration>(CacheConfig.SYSTEM_CONFIG.key);
      const merged = { ...(cached.data ?? {}), region };
      CacheService.set(CacheConfig.SYSTEM_CONFIG.key, merged, CacheConfig.SYSTEM_CONFIG.ttl);
    } catch {
      /* cache là tối ưu, lỗi ghi cache không ảnh hưởng kết quả trả về */
    }
    return region;
  } catch {
    return null;
  }
};
